(() => {
  'use strict';

  const ROOT_CLASS = 'prspr-rebuy-purchase-options';
  const ITEM_SELECTOR = '.rebuy-cart__flyout-item';
  const LADDER_DAYS = [30, 60, 90];
  // Shopify native fixed bundles (`requiresComponents=true`) reject every
  // selling-plan cart mutation with HTTP 422. Keep the one-time bundle line
  // purchasable, but do not render a subscription control that Shopify cannot
  // honor. The supported recurring-bundle path is Seal + Bundler.
  const NATIVE_FIXED_BUNDLE_HANDLES = new Set([
    'essentials-supplements-i-believe-everyone-should-take',
    'natures-bbl',
    'root-saw',
    'mens-vitality-bundle',
    'fermented-beet-root-maca-3-pack-bundle',
    'root-revive-hair-oil-3-pack-bundle',
    'natures-bbl-max',
    'adult-immunity-bundle',
    'anti-anxiety-bundle',
    'womens-essentials',
    'mens-essentials',
    'hair-growth-essentials',
    'anti-inflammation-bundle',
    'pre-post-natal-bundle',
    'menopause-perimenopause-bundle',
    'prspr-love-bundle',
    'wellness-foundation-stack',
    'energy-focus-stack',
    'hair-skin-stack',
    'sleep-recovery-stack'
  ]);
  const productCache = new Map();
  let renderTimer = 0;
  let renderInFlight = false;
  let changeInFlight = false;
  let lastCartSignature = '';

  function route(path) {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${String(path).replace(/^\//, '')}`;
  }

  function normalizeDays(plan) {
    const optionText = (plan?.options || []).map((option) => option?.value || '').join(' ').trim().toLowerCase();
    const text = optionText || String(plan?.name || '').trim().toLowerCase();
    if (!text) return null;
    if (/^(month|monthly|1\s*month)$/.test(text)) return 30;
    let match = text.match(/(\d+)\s*days?/);
    if (match) return Number(match[1]);
    match = text.match(/(\d+)\s*months?/);
    if (match) return Number(match[1]) * 30;
    match = text.match(/(\d+)\s*weeks?/);
    if (match) {
      const weeks = Number(match[1]);
      if (weeks === 4) return 30;
      return weeks * 7;
    }
    if (/every\s*month|monthly/.test(text)) return 30;
    return null;
  }

  function money(cents, currency = 'USD') {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function plansForVariant(product, variantId) {
    const variant = (product?.variants || []).find((candidate) => Number(candidate.id) === Number(variantId));
    if (!variant) return [];
    const allocations = new Map((variant.selling_plan_allocations || []).map((allocation) => [
      Number(allocation.selling_plan_id),
      allocation
    ]));
    const byDays = new Map();
    for (const group of (product.selling_plan_groups || [])) {
      for (const plan of (group.selling_plans || [])) {
        const id = Number(plan.id);
        const days = normalizeDays(plan);
        if (!allocations.has(id) || !LADDER_DAYS.includes(days) || byDays.has(days)) continue;
        const allocation = allocations.get(id);
        byDays.set(days, {
          id,
          days,
          name: plan.name,
          price: Number(allocation.price),
          compareAtPrice: Number(allocation.compare_at_price),
          perDeliveryPrice: Number(allocation.per_delivery_price),
          savingsPercent: Number(plan.price_adjustments?.find((adjustment) => adjustment.value_type === 'percentage')?.value || 20)
        });
      }
    }
    return LADDER_DAYS.map((days) => byDays.get(days)).filter(Boolean);
  }

  function currentPlanId(item) {
    return Number(item?.selling_plan_allocation?.selling_plan?.id || 0) || null;
  }

  function currentDays(item, plans) {
    const id = currentPlanId(item);
    return plans.find((plan) => plan.id === id)?.days || normalizeDays(item?.selling_plan_allocation?.selling_plan) || null;
  }

  function cartSignature(cart) {
    return (cart.items || []).map((item) => [
      item.key,
      item.quantity,
      currentPlanId(item) || 'one-time',
      item.final_price,
      item.final_line_price,
      item.line_level_total_discount
    ].join(':')).join('|');
  }

  async function fetchCart() {
    const response = await fetch(route('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
    return response.json();
  }

  async function fetchProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(route(`products/${encodeURIComponent(handle)}.js`), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then((response) => {
        if (!response.ok) throw new Error(`Product subscription data failed (${response.status})`);
        return response.json();
      }).catch((error) => {
        productCache.delete(handle);
        throw error;
      }));
    }
    return productCache.get(handle);
  }

  function lineElements() {
    return [...document.querySelectorAll(ITEM_SELECTOR)];
  }

  function handleFromElement(element) {
    const href = element.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    return href.match(/\/products\/([^?#/]+)/)?.[1] || null;
  }

  function pairCartItemsToElements(cart) {
    const queues = new Map();
    for (const [index, item] of (cart.items || []).entries()) {
      const queue = queues.get(item.handle) || [];
      queue.push({ item, lineIndex: index + 1 });
      queues.set(item.handle, queue);
    }
    const pairs = [];
    for (const element of lineElements()) {
      const handle = handleFromElement(element);
      const match = handle ? queues.get(handle)?.shift() : null;
      if (match) pairs.push({ element, ...match });
    }
    return pairs;
  }

  function optionButton({ mode, label, checked, disabled = false }) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'prspr-rpo__option';
    button.dataset.mode = mode;
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', String(checked));
    button.disabled = disabled;
    button.innerHTML = `<span class="prspr-rpo__dot" aria-hidden="true"></span><span>${label}</span>`;
    return button;
  }

  function buildControl(item, plans, currency, lineIndex) {
    const subscribed = Boolean(currentPlanId(item));
    const selectedDays = currentDays(item, plans);
    const defaultPlan = plans.find((plan) => plan.days === 30) || plans[0];
    const selectedPlan = plans.find((plan) => plan.days === selectedDays) || defaultPlan;
    const savings = selectedPlan?.savingsPercent || 20;
    const root = document.createElement('fieldset');
    root.className = ROOT_CLASS;
    root.dataset.lineKey = item.key;
    root.dataset.lineIndex = String(lineIndex);
    root.dataset.variantId = String(item.variant_id);
    root.dataset.handle = item.handle;
    root.dataset.currentPlanId = String(currentPlanId(item) || '');
    root.innerHTML = '<legend class="prspr-rpo__legend">Purchase options</legend>';

    const options = document.createElement('div');
    options.className = 'prspr-rpo__options';
    options.setAttribute('role', 'radiogroup');
    options.setAttribute('aria-label', `Purchase options for ${item.product_title}`);
    options.append(
      optionButton({ mode: 'subscription', label: `Subscribe & Save — Save ${savings}%`, checked: subscribed }),
      optionButton({ mode: 'one-time', label: 'One-Time Purchase', checked: !subscribed })
    );
    root.append(options);

    const frequency = document.createElement('div');
    frequency.className = 'prspr-rpo__frequency';
    frequency.hidden = !subscribed;
    frequency.innerHTML = '<span class="prspr-rpo__frequency-label">Deliver every:</span>';
    const frequencyOptions = document.createElement('div');
    frequencyOptions.className = 'prspr-rpo__frequency-options';
    frequencyOptions.setAttribute('role', 'radiogroup');
    frequencyOptions.setAttribute('aria-label', `Delivery frequency for ${item.product_title}`);
    for (const days of LADDER_DAYS) {
      const plan = plans.find((candidate) => candidate.days === days);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'prspr-rpo__frequency-button';
      button.dataset.days = String(days);
      button.dataset.planId = plan ? String(plan.id) : '';
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(subscribed && selectedDays === days));
      button.textContent = `${days} days`;
      button.disabled = !plan;
      if (!plan) button.title = 'Not available for this product';
      frequencyOptions.append(button);
    }
    frequency.append(frequencyOptions);
    root.append(frequency);

    const price = document.createElement('div');
    price.className = 'prspr-rpo__price-note';
    const oneTimePrice = subscribed
      ? Number(item.selling_plan_allocation?.compare_at_price || item.original_price || item.price)
      : Number(item.original_price || item.price);
    const subscriptionPrice = Number(selectedPlan?.price || item.selling_plan_allocation?.price || 0);
    price.textContent = subscribed
      ? `${money(subscriptionPrice, currency)} each · ${savings}% subscription savings`
      : `${money(oneTimePrice, currency)} each`;
    root.append(price);

    const status = document.createElement('div');
    status.className = 'prspr-rpo__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    root.append(status);
    return root;
  }

  function setBusy(control, busy, message = '') {
    control.classList.toggle('is-loading', busy);
    control.querySelectorAll('button').forEach((button) => { button.disabled = busy || !button.dataset.planId && button.classList.contains('prspr-rpo__frequency-button'); });
    const status = control.querySelector('.prspr-rpo__status');
    status.textContent = message;
    control.setAttribute('aria-busy', String(busy));
  }

  async function refreshRebuy(cart) {
    if (window.Rebuy?.Cart?.fetchCart) {
      await new Promise((resolve) => {
        let settled = false;
        const done = () => { if (!settled) { settled = true; resolve(); } };
        window.Rebuy.Cart.fetchCart(() => window.setTimeout(done, 120));
        window.setTimeout(done, 3000);
      });
    }
  }

  async function changeSellingPlan(control, sellingPlan) {
    if (changeInFlight) return;
    changeInFlight = true;
    setBusy(control, true, 'Updating…');
    try {
      const latest = await fetchCart();
      const expectedPlanId = Number(control.dataset.currentPlanId || 0) || null;
      const lineIndex = Number(control.dataset.lineIndex);
      const item = latest.items[lineIndex - 1];
      if (!item
        || item.key !== control.dataset.lineKey
        || Number(item.variant_id) !== Number(control.dataset.variantId)
        || item.handle !== control.dataset.handle
        || currentPlanId(item) !== expectedPlanId) {
        throw new Error('This cart item changed. Please try again.');
      }
      const response = await fetch(route('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          line: lineIndex,
          quantity: item.quantity,
          selling_plan: sellingPlan === null ? null : Number(sellingPlan)
        })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || cart.message || `Cart update failed (${response.status})`);
      await refreshRebuy(cart);
      lastCartSignature = cartSignature(cart);
      // Re-enable interaction before mounting the refreshed control so a quick
      // 30 → 60 → 90 selection cannot be dropped during the DOM replacement.
      changeInFlight = false;
      await renderCart(cart);
    } catch (error) {
      setBusy(control, false, error.message || 'Unable to update. Please try again.');
    } finally {
      changeInFlight = false;
    }
  }

  async function handleClick(event) {
    const button = event.target.closest(`.${ROOT_CLASS} button`);
    if (!button) return;
    const control = button.closest(`.${ROOT_CLASS}`);
    if (!control || button.disabled) return;
    if (button.dataset.mode === 'one-time') {
      await changeSellingPlan(control, null);
      return;
    }
    if (button.dataset.mode === 'subscription') {
      const selected = control.querySelector('.prspr-rpo__frequency-button[aria-checked="true"]');
      const first = [...control.querySelectorAll('.prspr-rpo__frequency-button')].find((candidate) => candidate.dataset.planId);
      await changeSellingPlan(control, selected?.dataset.planId || first?.dataset.planId);
      return;
    }
    if (button.dataset.planId) await changeSellingPlan(control, button.dataset.planId);
  }

  async function renderAll(force = false) {
    if (renderInFlight || changeInFlight || !document.querySelector('.rebuy-cart__flyout')) return;
    renderInFlight = true;
    try {
      const cart = await fetchCart();
      const signature = cartSignature(cart);
      if (!force && signature === lastCartSignature && document.querySelectorAll(`.${ROOT_CLASS}`).length) return;
      lastCartSignature = signature;
      await renderCart(cart);
    } catch (_) {
      // Rebuy remains fully usable if this enhancement cannot load.
    } finally {
      renderInFlight = false;
    }
  }

  async function renderCart(cart) {
      const pairs = pairCartItemsToElements(cart);
      const seenLogicalLines = new Set();
      const renderPairs = pairs.map((pair) => {
        const properties = Object.entries(pair.item.properties || {})
          .sort(([left], [right]) => left.localeCompare(right));
        const logicalKey = `${pair.item.variant_id}:${currentPlanId(pair.item) || 'one-time'}:${JSON.stringify(properties)}`;
        const duplicateLogicalLine = seenLogicalLines.has(logicalKey);
        seenLogicalLines.add(logicalKey);
        return { ...pair, duplicateLogicalLine };
      });
      await Promise.all(renderPairs.map(async ({ element, item, lineIndex, duplicateLogicalLine }) => {
        try {
          const product = await fetchProduct(item.handle);
          const plans = plansForVariant(product, item.variant_id);
          element.querySelector(`.${ROOT_CLASS}`)?.remove();
          if (NATIVE_FIXED_BUNDLE_HANDLES.has(item.handle)) return;
          if (!plans.length) return;
          // Membership Club lines: the refill cadence is matched to the bundle's supply on the offer page and the
          // first-order code is tied to that plan, so the Subscribe/One-time + frequency switcher is not offered here.
          if (item.properties && item.properties._prspr_membership_club) return;
          const control = buildControl(item, plans, cart.currency || 'USD', lineIndex);
          control.classList.toggle('prspr-rpo--duplicate-logical-line', duplicateLogicalLine);
          if (duplicateLogicalLine) control.setAttribute('aria-label', 'Purchase options shown with the matching item above');
          const quantity = element.querySelector('.rebuy-cart__flyout-item-quantity');
          const info = element.querySelector('.rebuy-cart__flyout-item-info');
          if (quantity) quantity.before(control);
          else info?.append(control);
        } catch (_) {
          // A failed product lookup must not interfere with the cart drawer.
        }
      }));
  }

  function schedule(force = false) {
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => renderAll(force), 100);
  }

  document.addEventListener('click', handleClick);
  document.addEventListener('cart:updated', () => schedule(true));
  document.addEventListener('rebuy:cart.change', () => schedule(true));
  window.addEventListener('pageshow', () => schedule(true));
  new MutationObserver((mutations) => {
    if (mutations.some((mutation) => [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.(ITEM_SELECTOR) || node.querySelector?.(ITEM_SELECTOR))))) schedule(true);
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.__PRSPR_REBUY_PURCHASE_OPTIONS__ = {
    normalizeDays,
    plansForVariant,
    currentPlanId,
    currentDays,
    cartSignature,
    pairCartItemsToElements,
    buildControl,
    changeSellingPlan,
    fetchCart,
    fetchProduct,
    renderAll
  };
  schedule(true);
})();
